import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, combineLatest, forkJoin, Observable, of, Subscription, zip } from 'rxjs';
import {
  concatMap,
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  startWith,
  switchMap,
  take,
  tap,
  publishReplay,
  refCount
} from 'rxjs/operators';

import {
  DropdownItem,
  GetLocationsRequest,
  GetLocationsResponse,
  GetNavigationDataRequest,
  GetNavigationDataResponse,
  GetSalesInfoRequest,
  GetSalesInfoResponse,
  Location,
  PinnedItem,
  RetentionConfig,
  SalesContact,
  SideNavigationItem,
  Theming,
} from './atlas_sdk/api';
import { AtlasApiService } from './atlas_sdk/atlas.api.service';
import { UITheme, UserViewType } from './atlas_sdk/enums';
import { BusinessNavConfigService, Config } from './config.service';
import { ListElevatedLocationsRequestInterface } from './atlas_sdk/api.interface';

export const LOCATIONS_PAGE_SIZE = 50;

interface CachedGetNavigationDataResponse {
  locationId: string;
  response: GetNavigationDataResponse;
}

interface LoadMoreResponse {
  // Returned as an observable. If the the observable returns immediately, loading will either be an observable that
  // returns when the last request has resolved, or null if the last request has resolved but another was not started
  // (usually in the case of hasMore being false). If the observable does not resolve immediately it will return when
  // the request has resolved, and loading should be null.
  loading: Observable<boolean>;
  hasMore: boolean;
}

export enum LocationType {
  BUSINESS = 1,
  BRAND = 2,
}

export interface LocationCounts {
  businesses: number;
  brands: number;
}

@Injectable({providedIn: 'root'})
export class BusinessNavDataService implements OnDestroy {
  private navigationData$: Observable<GetNavigationDataResponse>;
  private salesInfo$: Observable<GetSalesInfoResponse>;
  public businessCenterName$: Observable<string>;
  public partnerName$: Observable<string>;
  public cobrandingLogoURL$: Observable<string>;
  public marketName$: Observable<string>;
  public marketLogoUrl$: Observable<string>;
  public retentionConfig$: Observable<RetentionConfig>;
  public salespersonName$: Observable<string>;
  public salespersonPictureUrl$: Observable<string>;
  public currentBrandName$: Observable<string>;
  public salespersonContact$: Observable<SalesContact>;
  public theme$: Observable<UITheme>;
  public theming$: Observable<Theming>;
  public businessNavigationItems$: Observable<SideNavigationItem[]>;
  public brandNavigationItems$: Observable<SideNavigationItem[]>;
  public dropdownItems$: Observable<DropdownItem[]>;
  public pinnedItems$: Observable<PinnedItem[]>;
  public language$: Observable<string>;
  public userViewType$: Observable<UserViewType>;
  public businessAppBranding$: Observable<boolean>;
  public doneLoadingSMBBusinesses: boolean = false;
  public doneLoadingSMBBrands: boolean = false;


  private loadedBusinesses$$: BehaviorSubject<Location[]> = new BehaviorSubject([]);
  public loadedBusinesses$: Observable<Location[]> = this.loadedBusinesses$$.asObservable();
  private loadedBrands$$: BehaviorSubject<Location[]> = new BehaviorSubject([]);
  public loadedBrands$: Observable<Location[]> = this.loadedBrands$$.asObservable();
  public loadedLocations$: Observable<Location[]>;
  public userId$: Observable<string>;

  private loadingBusinesses$$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public loadingBusinesses$: Observable<boolean> = this.loadingBusinesses$$.asObservable();
  private hasMoreBusinesses: boolean = true;
  private pageBusinesses: number = 0;
  private cursorBusinesses: string = '';

  private loadingBrands$$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public loadingBrands$: Observable<boolean> = this.loadingBusinesses$$.asObservable();
  private hasMoreBrands: boolean = true;
  private pageBrands: number = 0;
  private cursorBrands: string = '';

  private locationSearch$$: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  private locationSearch$: Observable<string> = this.locationSearch$$.asObservable();

  private locationCounts$$: BehaviorSubject<LocationCounts> = new BehaviorSubject<LocationCounts>({ brands: 0, businesses: 0 });
  public locationCounts$: Observable<LocationCounts> = this.locationCounts$$.asObservable();

  public defaultLocation$$: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  public defaultLocation$: Observable<string>;
  private associatedAccountGroupIDs$: Observable<string[]>;
  private batchedBusinessRequests$: Observable<GetLocationsRequest[]>;
  private batchedBrandRequests$: Observable<GetLocationsRequest[]>;
  private associatedBrandPaths$: Observable<string[]>;

  private subscriptions: Subscription[] = [];

  constructor(private apiService: AtlasApiService, private cfgService: BusinessNavConfigService) {
    this.navigationData$ = this.cfgService.config$
      .pipe(
        distinctUntilChanged(),
        tap((config) => {
          if (config.accountId) {
            this.loadSingleLocation(config.accountId, true);
          } else {
            this.loadSingleLocation(config.groupPath, false);
          }
        }),
        switchMap((config: Config) => this.apiService.getNavigationData(new GetNavigationDataRequest({
          accountGroupId: config.accountId,
          groupPath: config.groupPath,
          partnerId: config.partnerId,
          marketId: config.marketId
        })).pipe(
          filter((res: GetNavigationDataResponse) => JSON.stringify(res.toApiJson()) !== '{}'),
          map((res: GetNavigationDataResponse) => {
            const cache = {
              locationId: this.locationIdFromConfig(config),
              response: res
            };
            this.rememberLastResponse(cache);
            return cache;
          }),
          startWith(this.getLastResponse()),
          filter((res: CachedGetNavigationDataResponse) => !!res && res.locationId === this.locationIdFromConfig(config)),
          map((res: CachedGetNavigationDataResponse) => res.response)
        )),
        shareReplay(1)
      );

      this.salesInfo$ = this.cfgService.config$
      .pipe(
        distinctUntilChanged(),
        tap((config) => {
          if (config.accountId) {
            this.loadSingleLocation(config.accountId, true);
          } else {
            this.loadSingleLocation(config.groupPath, false);
          }
        }),
        switchMap((config: Config) => this.apiService.getSalesInfo(new GetSalesInfoRequest({
          accountGroupId: config.accountId,
          groupPath: config.groupPath,
        })).pipe(
          filter((res: GetSalesInfoResponse) => JSON.stringify(res.toApiJson()) !== '{}'),
        )),
        shareReplay(1)
      );

    this.dropdownItems$ = this.navigationData$
      .pipe(
        map(res => {
          if (!!res.dropdownItems) {
            return res.dropdownItems;
          }
          return [];
        }),
        shareReplay(1),
      );

    this.businessCenterName$ = this.navigationData$
      .pipe(
        filter(res => !!res.branding),
        map(res => res.branding.centerName),
        shareReplay(1)
      );

    this.marketLogoUrl$ = this.navigationData$
      .pipe(
        filter(res => !!res.branding),
        map(res => res.branding.logoUrl),
        shareReplay(1)
      );

    this.retentionConfig$ = this.navigationData$
      .pipe(
        filter(res => !!res.retentionConfig),
        map(res => res.retentionConfig),
        shareReplay(1)
      );

    this.theme$ = this.navigationData$
      .pipe(
        startWith({ branding: { theme: this.getLastTheme() } }),
        map(res => {
          return (typeof res.branding.theme === 'undefined') ? UITheme.UI_THEME_DARK : res.branding.theme;
        }),
        tap((theme: UITheme) => {
          this.rememberLastTheme(theme);
        }),
        shareReplay(1),
      );

    this.theming$ = this.navigationData$
      .pipe(
        map(res => {
          // Ensure that theming exists and has properties
          if (!!res && !!res.branding && !!res.branding.theming && (res.branding.theming.primaryColor !== undefined)) {
            return res.branding.theming;
          }
          if (!!res && !!res.branding && (res.branding.theme === UITheme.UI_THEME_LIGHT)) {
            // no theming, but light theme is set
            return new Theming({
              primaryColor: '#fff',
              primaryHoverColor: '#f0f0f0',
              primaryActiveColor: '#e1e1e1',
              secondaryColor: 'rgba(0, 0, 0, .04)',
              secondaryHoverColor: 'rgba(0, 0, 0, .04)',
              secondaryActiveColor: 'rgba(0, 0, 0, .1)',
              fontColor: 'rgba(0, 0, 0, 0.54)',
              fontDisabledColor: 'rgba(0, 0, 0, 0.25)',
              accentsColor: '#000',
              accentsActiveColor: '#057ec1',
              focusColor: '#bbdefb',
              borderColor: '#dadada',
            });
          } else {
            // dark theme is set
            return new Theming(
              {
                primaryColor: '#212121',
                primaryHoverColor: '#303030',
                primaryActiveColor: '#3f3f3f',
                secondaryColor: 'rgba(255, 255, 255, .04)',
                secondaryHoverColor: 'rgba(255, 255, 255, .04)',
                secondaryActiveColor: 'rgba(255, 255, 255, .1)',
                fontColor: 'rgba(255, 255, 255, 0.7)',
                fontDisabledColor: 'rgba(255,255,255, 0.50)',
                accentsColor: '#fff',
                accentsActiveColor: '#057ec1',
                focusColor: '#616161',
                borderColor: '#9a9b9a',
              });
          }
        }),
        distinctUntilChanged(),
        shareReplay(1),
      );

    this.partnerName$ = this.navigationData$
      .pipe(
        filter(res => !!res.branding),
        map(res => res.branding.partnerName),
        shareReplay(1)
      );

      this.cobrandingLogoURL$ = this.navigationData$
      .pipe(
        filter(res => !!res.branding),
        map(res => res.branding.cobrandingLogoUrl),
        shareReplay(1)
      );

    this.marketName$ = this.navigationData$
      .pipe(
        filter(res => !!res.branding),
        map(res => res.branding.marketName),
        shareReplay(1)
      );

    this.salespersonName$ = this.salesInfo$
      .pipe(
        filter(res => !!res.salesInfo && !!res.salesInfo.salesContact),
        map(res => res.salesInfo.salesContact.firstName + ((!!res.salesInfo.salesContact.lastName) ? ' ' + res.salesInfo.salesContact.lastName : '')),
        shareReplay(1)
      );

    this.salespersonPictureUrl$ = this.salesInfo$
      .pipe(
        filter(res => !!res.salesInfo && !!res.salesInfo.salesContact),
        map(res => res.salesInfo.salesContact.photoUrlSecure),
        startWith('https://lh3.googleusercontent.com/70DGcv8fyAzy1n_cWh8GyVbsCIWzPGZMdBFiwJa8Ot-Ss5Rf_oZY8hKOG2wAttY4_iuU5TlO9NLlDrBGuEJBj6DHwRnGd2CJ32J2Im3c1g'),
        shareReplay(1)
      );

    this.salespersonContact$ = this.salesInfo$
      .pipe(
        filter(res => !!res.salesInfo && !!res.salesInfo.salesContact),
        map(res => res.salesInfo.salesContact),
        shareReplay(1)
      );

    this.brandNavigationItems$ = this.navigationData$
      .pipe(
        map(res => res.brandItems),
        shareReplay(1)
      );

    this.businessNavigationItems$ = this.navigationData$
      .pipe(
        map(res => res.accountItems),
        shareReplay(1)
      );

    this.pinnedItems$ = this.navigationData$
      .pipe(
        map(res => res.pinnedItems),
        shareReplay(1),
      );

    this.language$ = this.navigationData$
      .pipe(
        map(res => res.language),
        shareReplay(1),
      );

    this.currentBrandName$ = this.navigationData$
      .pipe(
        map(res => res.currentBrandName),
        shareReplay(1),
      );

    this.userViewType$ = this.navigationData$
      .pipe(
        map(res => {
          if (!res.userView) {
            return UserViewType.USER_VIEW_TYPE_SMB;
          }
          return res.userView;
        }),
        shareReplay(1),
      );

    this.defaultLocation$ = combineLatest([this.navigationData$, this.defaultLocation$$.asObservable()])
      .pipe(
        map(([res, defaultLocationOverride]: [GetNavigationDataResponse, string]) => !!defaultLocationOverride ? defaultLocationOverride : res.defaultLocation),
        shareReplay(1)
      );

    this.associatedAccountGroupIDs$ = this.navigationData$
      .pipe(
        map((resp: GetNavigationDataResponse) => resp.associatedLocationIds.accountGroupIds),
        distinctUntilChanged(),
        shareReplay(1),
      );

    this.associatedBrandPaths$ = this.navigationData$
      .pipe(
        map((resp: GetNavigationDataResponse) => resp.associatedLocationIds.groupPaths),
        distinctUntilChanged(),
        shareReplay(1)
      );

    this.locationCounts$ = this.navigationData$.pipe(
      map((data): LocationCounts => {
        return data.totalLocations
          ? { businesses: data.totalLocations.accounts, brands: data.totalLocations.brands }
          : { businesses: 0, brands: 0 };
      })
    );

    const accountGroupIdsToFetch$ = combineLatest(this.associatedAccountGroupIDs$, this.cfgService.accountGroupId$).pipe(
      map(([associatedAccountGroupIds, currentAccountGroupId]: [string[], string]) => {
        let associated = [];
        if (!!associatedAccountGroupIds) {
          associated = associatedAccountGroupIds.slice(0);
        }
        if (currentAccountGroupId && associated.indexOf(currentAccountGroupId) === -1) {
          associated.push(currentAccountGroupId);
        }
        return associated;
      }),
    );

    const brandPathsToFetch$ = combineLatest(this.associatedBrandPaths$, this.cfgService.groupPath$).pipe(
      map(([associatedBrands, currentBrand]: [string[], string]) => {
        let associated = [];
        if (!!associatedBrands) {
          associated = associatedBrands.slice(0);
        }
        if (!currentBrand) {
          return associated;
        }
        if (associated.indexOf(currentBrand) === -1) {
          associated.push(currentBrand);
        }
      })
    );

    this.batchedBusinessRequests$ = this.batchRequests(accountGroupIdsToFetch$, LocationType.BUSINESS).pipe(
      distinctUntilChanged(),
      shareReplay(1),
    );
    this.batchedBrandRequests$ = this.batchRequests(brandPathsToFetch$, LocationType.BRAND).pipe(
      distinctUntilChanged(),
      shareReplay(1),
    );

    this.loadedLocations$ = combineLatest(this.loadedBusinesses$, this.loadedBrands$).pipe(
      map(([businesses, brands]) => {
        return [...businesses, ...brands].sort((a, b) => {
          // Sorting is intentionally case sensitive because the database sorting is case sensitive and we want to
          // maintain the order so new locations are added to the end of the list.
          const nameA = a.accountGroup ? a.accountGroup.name : a.brand.name;
          const nameB = b.accountGroup ? b.accountGroup.name : b.brand.name;
          return (nameA > nameB) ? 1 : -1;
        });
      })
    );

    this.userId$ = this.navigationData$
        .pipe(
            map((resp: GetNavigationDataResponse) => resp.userId),
            distinctUntilChanged(),
            publishReplay(1),
            refCount()
        );

    this.businessAppBranding$ = this.navigationData$.pipe(
      map(data => data.businessAppBranding),
      shareReplay(1)
    );

    this.subscriptions.push(
      zip(this.batchedBusinessRequests$, this.userViewType$).pipe(
        filter(([_, viewType]) => viewType !== UserViewType.USER_VIEW_TYPE_ADMIN),
        take(1),
        switchMap(() => this.loadMore(LocationType.BUSINESS))
      ).subscribe(),
      zip(this.batchedBrandRequests$, this.userViewType$).pipe(
        filter(([_, viewType]) => viewType !== UserViewType.USER_VIEW_TYPE_ADMIN),
        take(1),
        switchMap(() => this.loadMore(LocationType.BRAND))
      ).subscribe(),
      this.userViewType$.pipe(
        filter(userView => userView === UserViewType.USER_VIEW_TYPE_ADMIN),
        distinctUntilChanged(),
        switchMap(() => {
          // This triggers an initial load on userViewType being set to prevent the case where due to the asynchronous
          // execution of observables the `hasMore` values are set to false based on the SMB data before this value is set.
          this.hasMoreBusinesses = true;
          this.hasMoreBrands = true;
          return forkJoin(this.loadMore(LocationType.BUSINESS), this.loadMore(LocationType.BRAND));
        }),
      ).subscribe(),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  private distinctListComparator(prev: string[], cur: string[]): boolean {
    if (prev === undefined) {
      prev = [];
    }
    if (cur === undefined) {
      cur = [];
    }
    if (prev.length !== cur.length) {
      return false;
    }
    for (let i = 0; i < prev.length; i++) {
      if (prev[i] !== cur[i]) {
        return false;
      }
    }
    return true;
  }

  private rememberLastResponse(res: CachedGetNavigationDataResponse): void {
    localStorage.setItem('business-nav-last-response', JSON.stringify({
      'locationId': res.locationId,
      'response': res.response.toApiJson(),
    }));
  }

  private batchRequests(ids$: Observable<string[]>, locationType: LocationType): Observable<GetLocationsRequest[]> {
    return ids$.pipe(
      filter((ids: string[]) => !!ids),
      distinctUntilChanged((prev, cur) => this.distinctListComparator(prev, cur)),
      map((ids: string[]) => {
        let locationBatches: GetLocationsRequest[] = [];
        if (!!ids) {
          locationBatches = this.buildGetLocationBatchRequests(
            ids,
            locationType === LocationType.BRAND
              ? this.buildGetBrandsBatchRequest
              : this.buildGetAccountGroupsBatchRequest
          );
        }
        return locationBatches;
      }),
      shareReplay(1)
    );
  }

  private getLastResponse(): CachedGetNavigationDataResponse {
    const o: { locationId: string, response: object } = JSON.parse(localStorage.getItem('business-nav-last-response'));
    if (!o || !o.response) {
      return null;
    }
    return {
      locationId: o.locationId,
      response: GetNavigationDataResponse.fromProto(o.response),
    };
  }

  private rememberLastTheme(theme: UITheme): void {
    localStorage.setItem('LAST_THEME', theme.toString());
  }

  private getLastTheme(): number {
    const t = localStorage.getItem('LAST_THEME');
    if (!t) {
      return UITheme.UI_THEME_LIGHT;
    }
    return +t;
  }

  private locationIdFromConfig(cfg: Config): string {
    return (!!cfg.accountId) ? cfg.accountId : cfg.groupPath;
  }

  private getName(location: Location): string {
    if (!!location.accountGroup) {
      return location.accountGroup.name;
    }
    return location.brand.name;
  }

  public sortLocations(locs: Location[]): Location[] {
    return locs.sort(this.sort.bind(this));
  }

  private sort(l1: Location, l2: Location): number {
    return this.getName(l1).toUpperCase() < this.getName(l2).toUpperCase() ? -1 : 1;
  }

  private getLocations(req: GetLocationsRequest): Observable<GetLocationsResponse> {
    return this.apiService.getLocations(req);
  }

  private doLocationsMatch(l1: Location, l2: Location): boolean {
    // For a valid case, they must both be valid values
    if (!l1 || !l2) {
      return false;
    }
    if (!!l1.accountGroup && !!l2.accountGroup && l1.accountGroup.accountGroupId === l2.accountGroup.accountGroupId) {
      return true;
    }
    if (!!l1.brand && !!l2.brand) {
      // Are the brands actually the same? Check to see if the last node (child) is the same
      if (l1.brand.pathNodes[l1.brand.pathNodes.length - 1] === l2.brand.pathNodes[l2.brand.pathNodes.length - 1]) {
        return true;
      }
    }
    return false;
  }

  private buildGetLocationBatchRequests(toBatchIds: string[], batchBuilder: (batch: string[]) => GetLocationsRequest): GetLocationsRequest[] {
    const reqs = [];
    if (!!toBatchIds && toBatchIds.length === 0) {
      return reqs;
    }
    while (true) {
      if (toBatchIds.length > LOCATIONS_PAGE_SIZE) {
        reqs.push(batchBuilder(toBatchIds.slice(0, LOCATIONS_PAGE_SIZE)));
        toBatchIds = toBatchIds.slice(LOCATIONS_PAGE_SIZE, toBatchIds.length);
        continue;
      }
      reqs.push(batchBuilder(toBatchIds.slice(0, toBatchIds.length)));
      // we've reached the end of the ids
      break;
    }
    return reqs;
  }

  private buildGetAccountGroupsBatchRequest(batch: string[]): GetLocationsRequest {
    return new GetLocationsRequest({
      accountGroups: { accountGroupIds: batch }
    });
  }

  private buildGetBrandsBatchRequest(batch: string[]): GetLocationsRequest {
    return new GetLocationsRequest({
      groups: { groupPaths: batch }
    });
  }

  public setOverridenDefaultLocation(defaultLocationOverride: string): void {
    this.defaultLocation$$.next(defaultLocationOverride);
  }

  public loadMore(locationType: LocationType): Observable<LoadMoreResponse> {
    return this.userViewType$.pipe(
      take(1),
      concatMap(viewType => {
        const loadBusinesses: boolean = (locationType === LocationType.BUSINESS);
        if (loadBusinesses ? !!this.loadingBusinesses$$.value : !!this.loadingBrands$$.value) {
          // return if currently loading
          return of({
            hasMore: this.hasMore(loadBusinesses, viewType === UserViewType.USER_VIEW_TYPE_ADMIN),
            loading: (loadBusinesses ? this.loadingBusinesses$ : this.loadingBrands$).pipe(filter(x => !x), take(1))
          });
        }
        if ((loadBusinesses ? !this.hasMoreBusinesses : !this.hasMoreBrands) && !(viewType === UserViewType.USER_VIEW_TYPE_ADMIN && !(loadBusinesses ? this.doneLoadingSMBBusinesses : this.doneLoadingSMBBrands))) {
          // return if no more to load
          return of({
            loading: null,
            hasMore: false,
          });
        }
        if ((loadBusinesses ? !this.hasMoreBusinesses : !this.hasMoreBrands) && viewType === UserViewType.USER_VIEW_TYPE_ADMIN && !(loadBusinesses ? this.doneLoadingSMBBusinesses : this.doneLoadingSMBBrands)) {
          if (loadBusinesses) {
            this.hasMoreBusinesses = true;
            this.doneLoadingSMBBusinesses = true;
          } else {
            this.hasMoreBrands = true;
            this.doneLoadingSMBBrands = true;
          }
        }
        // set loading status
        (loadBusinesses ? this.loadingBusinesses$$ : this.loadingBrands$$).next(true);
        // use elevated loading if necessary
        if (viewType === UserViewType.USER_VIEW_TYPE_ADMIN && (loadBusinesses ? this.doneLoadingSMBBusinesses : this.doneLoadingSMBBrands)) {
          return this._elevatedLoadMore(loadBusinesses);
        } else {
          return this._smbLoadMore(loadBusinesses, viewType === UserViewType.USER_VIEW_TYPE_ADMIN);
        }
      }),
    );
  }

  private _smbLoadMore(loadBusinesses: boolean, admin: boolean): Observable<LoadMoreResponse> {
    return (loadBusinesses ? this.batchedBusinessRequests$ : this.batchedBrandRequests$).pipe(
      filter((requests: GetLocationsRequest[]): boolean => {
        return requests !== [];
      }),
      take(1),
      concatMap((requests: GetLocationsRequest[]): Observable<GetLocationsResponse> => {
        const page = (loadBusinesses ? this.pageBusinesses : this.pageBrands);
        if (loadBusinesses) {
          this.pageBusinesses = page + 1;
          this.hasMoreBusinesses = (page <= requests.length);
        } else {
          this.pageBrands = page + 1;
          this.hasMoreBrands = (page <= requests.length);
        }
        if (page < requests.length) {
          return this.getLocations(requests[page]);
        } else {
          return of(new GetLocationsResponse({locations: []}));
        }
      }),
      map((resp): LoadMoreResponse => {
        const locs = resp.locations;
        (loadBusinesses ? this.loadingBusinesses$$ : this.loadingBrands$$).next(false);
        if (locs.length <= 0) {
          return {
            loading: null,
            hasMore: this.hasMore(loadBusinesses, admin)
          };
        }
        this.addLocations(loadBusinesses, resp.locations);
        return {loading: null, hasMore: this.hasMore(loadBusinesses, admin)};
      }),
    );
  }

  private _elevatedLoadMore(loadBusinesses: boolean): Observable<LoadMoreResponse> {
    return forkJoin(
      this.cfgService.config$.pipe(take(1)),
      this.locationSearch$.pipe(take(1))
    ).pipe(
      concatMap(([config, search]) => {
        const req: ListElevatedLocationsRequestInterface = {
          search: search,
          cursor: (loadBusinesses ? this.cursorBusinesses : this.cursorBrands),
          pageSize: LOCATIONS_PAGE_SIZE,
          partnerId: config.partnerId,
        };
        if (loadBusinesses) {
          req.accountGroups = true;
        } else {
          req.brands = true;
        }
        return this.apiService.listElevatedLocations(req);
      }),
      map((resp) => {
        if (loadBusinesses) {
          this.cursorBusinesses = resp.cursor ? resp.cursor : '';
          this.hasMoreBusinesses = resp.hasMore ? resp.hasMore : false;
        } else {
          this.cursorBrands = resp.cursor ? resp.cursor : '';
          this.hasMoreBrands = resp.hasMore ? resp.hasMore : false;
        }
        this.addLocations(loadBusinesses, resp.locations);
        (loadBusinesses ? this.loadingBusinesses$$ : this.loadingBrands$$).next(false);
        return { loading: null, hasMore: resp.hasMore ? resp.hasMore : false };
      }),
    );
  }

  private addLocations(loadBusinesses: boolean, locations: Location[]): void {
    if (!locations || locations.length === 0) {
      return;
    }
    if (loadBusinesses) {
      this.loadedBusinesses$$.next([
        ...this.loadedBusinesses$$
          .getValue()
          .filter(oldLoc => !locations.find(loc => this.doLocationsMatch(oldLoc, loc))),
        ...locations
      ]);
    } else {
      this.loadedBrands$$.next([
        ...this.loadedBrands$$
          .getValue()
          .filter(oldLoc => !locations.find(loc => this.doLocationsMatch(oldLoc, loc))),
        ...locations
      ]);
    }
  }

  public updateSearch(value: string): void {
    combineLatest(this.loadingBusinesses$, this.userViewType$).pipe(
      filter(([x, y]) => !x),
      take(1),
      tap(([_, viewType]) => {
        this.locationSearch$$.next(value);
        if (viewType === UserViewType.USER_VIEW_TYPE_ADMIN) {
          this.cursorBusinesses = '';
          this.hasMoreBusinesses = true;
        }
      }),
    ).subscribe();
  }

  public loadSingleLocation(id: string, isAccountGroup: boolean = true): void {
    this.getLocations(
      isAccountGroup
        ? this.buildGetAccountGroupsBatchRequest([id])
        : this.buildGetBrandsBatchRequest([id])
    ).subscribe(resp => {
      this.addLocations(isAccountGroup, resp.locations);
    });
  }

  private hasMore(loadBusinesses: boolean, admin: boolean): boolean {
    if (admin) {
      return loadBusinesses ? !(!this.hasMoreBusinesses && this.doneLoadingSMBBusinesses) : !(!this.hasMoreBrands && this.doneLoadingSMBBrands);
    }
    return loadBusinesses ? this.hasMoreBusinesses : this.hasMoreBrands;
  }
}
